import api from '../../../utils/api';
import {
    createSingleFileFormData,
    mergeUploadResults,
    toFileArray
} from '@/utils/fileUploadQueue';

export default new class RequestsApi {
    /**
     * Получить счетчики заявок для всех вкладок
     * @returns {Promise<{my: number, requires_action: number, all: number, all_employees?: number}>}
     */
    getRequestCounts() {
        return api.get(`/api/requests/counts`).then(({data}) => data);
    }

    /**
     * Получить список заявок с пагинацией, сортировкой и фильтрами
     * @param {Object} opts
     * @param {string}  [opts.tab]        - Вкладка: 'my', 'requires_action', 'all', 'all_employees'
     * @param {number}  [opts.page]       - Страница (с 1)
     * @param {number}  [opts.per_page]   - Строк на страницу (1–100)
     * @param {string}  [opts.sort_by]    - Поле сортировки
     * @param {boolean} [opts.sort_desc]  - Обратный порядок
     * @param {string}  [opts.q]          - Поиск по тексту
     * @param {string}  [opts.status]     - Фильтр по статусу или badge-состоянию
     * @param {number|string} [opts.request_type_id] - Фильтр по типу заявки или category:<category>
     * @param {number|string} [opts.building_process_id] - Фильтр по объекту
     * @param {boolean} [opts.include_access_meta] - Добавить признак скрытых правами записей
     * @returns {Promise<{items: Array, total: number, page: number, per_page: number, badge_filter_options: Array, access_limited?: boolean}>}
     */
    getRequests({
        tab = null,
        page = 1,
        per_page = 20,
        sort_by = 'createdAt',
        sort_desc = false,
        q = '',
        status = '',
        request_type_id = '',
        building_process_id = '',
        include_access_meta = false,
        recurrence_kind = ''
    } = {}) {
        const params = { page, per_page, sort_by, sort_desc: String(sort_desc) };
        if (tab && tab !== 'all') params.tab = tab;
        if (q) params.q = q;
        if (recurrence_kind) params.recurrence_kind = recurrence_kind;
        if (status && status !== 'all') params.status = status;
        if (request_type_id && request_type_id !== 'all') params.request_type_id = request_type_id;
        if (building_process_id && building_process_id !== 'all') params.building_process_id = building_process_id;
        if (include_access_meta) params.include_access_meta = 'true';
        return api.get(`/api/requests/items`, { params }).then(({data}) => data);
    }

    /**
     * Получить объекты для фильтра списка заявок.
     */
    getRequestBuildingProcessFilterOptions() {
        return api.get(`/api/requests/items/filter-options/building-processes`).then(({data}) => data);
    }

    /**
     * Получить заявку по ID
     */
    getRequest(id) {
        return api.get(`/api/requests/items/${id}`).then(({data}) => data);
    }

    /**
     * Получить компактный список позиций заявки для предпросмотра в общем списке.
     */
    getRequestItemsPreview(id) {
        return api.get(`/api/requests/items/${id}/items-preview`).then(({data}) => data);
    }

    /**
     * Получить обязательные документы заявки для предпросмотра в общем списке.
     */
    getRequestRequiredAttachmentsPreview(id) {
        return api.get(`/api/requests/items/${id}/required-attachments-preview`).then(({data}) => data);
    }

    /**
     * Получить вкладку и страницу, на которой находится заявка в списке.
     * Используется для восстановления позиции при возврате с карточки.
     * @param {number|string} id - ID заявки
     * @param {Object} params - { per_page, sort_by, sort_desc, q, status, request_type_id, building_process_id }
     * @returns {Promise<{found: boolean, tab: string, page: number}>}
     */
    getRequestListContext(id, params = {}) {
        return api.get(`/api/requests/items/${id}/list-context`, { params }).then(({data}) => data);
    }

    /**
     * Создать новую заявку
     */
    createRequest(payload) {
        return api.post(`/api/requests/items`, payload).then(({data}) => data);
    }

    /**
     * Загрузить вложения к существующей заявке.
     * Каждый файл уходит отдельным multipart-запросом.
     */
    async uploadRequestFiles(id, files = []) {
        const results = [];
        for (const file of toFileArray(files)) {
            const formData = createSingleFileFormData('attachments', file);
            const {data} = await api.post(`/api/requests/items/${id}/files`, formData, {
                headers: {'Content-Type': 'multipart/form-data'}
            });
            results.push(data);
        }
        return mergeUploadResults(results);
    }

    /**
     * Загрузить обязательный документ к существующей заявке.
     */
    async uploadRequiredAttachment(id, fileKind, files = []) {
        const results = [];
        for (const file of toFileArray(files)) {
            const formData = createSingleFileFormData('attachments', file, {file_kind: fileKind});
            const {data} = await api.post(`/api/requests/items/${id}/required-attachments`, formData, {
                headers: {'Content-Type': 'multipart/form-data'}
            });
            results.push(data);
        }
        return mergeUploadResults(results);
    }

    /**
     * Обновить заявку
     */
    updateRequest(id, payload, options = {}) {
        return api.put(`/api/requests/items/${id}`, payload, options).then(({data}) => data);
    }

    /**
     * Обновить позицию заявки
     */
    updateRequestItem(id, itemId, payload) {
        return api.put(`/api/requests/items/${id}/items/${itemId}`, payload).then(({data}) => data);
    }

    /**
     * Удалить заявку
     */
    deleteRequest(id) {
        return api.delete(`/api/requests/items/${id}`).then(({data}) => data);
    }

    /**
     * Получить типы заявок
     */
    getRequestTypes() {
        return api.get(`/api/requests/types`).then(({data}) => data);
    }

    /**
     * Получить один тип заявки по ID
     */
    getRequestType(id) {
        return api.get(`/api/requests/types/${id}`).then(({data}) => data);
    }

    /**
     * Получить типы заявок для фильтра списка.
     * Не равно списку типов для создания: включает типы видимых пользователю заявок.
     */
    getRequestTypeFilterOptions() {
        return api.get(`/api/requests/types/filter-options`).then(({data}) => data);
    }

    /**
     * Создать тип заявки
     */
    createRequestType(payload) {
        return api.post(`/api/requests/types`, payload).then(({data}) => data);
    }

    /**
     * Обновить тип заявки
     */
    updateRequestType(id, payload) {
        return api.put(`/api/requests/types/${id}`, payload).then(({data}) => data);
    }

    /**
     * Удалить тип заявки
     */
    deleteRequestType(id) {
        return api.delete(`/api/requests/types/${id}`).then(({data}) => data);
    }

    /**
     * Согласовать заявку
     */
    approveRequest(id, comment = null) {
        const payload = comment ? { comment } : {};
        return api.post(`/api/requests/items/${id}/approve`, payload).then(({data}) => data);
    }

    /**
     * Отклонить заявку
     * @param {number} id - ID заявки
     * @param {string} comment - Причина отказа (обязательно)
     */
    rejectRequest(id, comment) {
        return api.post(`/api/requests/items/${id}/reject`, { comment }).then(({data}) => data);
    }

    /**
     * Взять заявку в работу
     */
    executeRequest(id) {
        return api.post(`/api/requests/items/${id}/execute`).then(({data}) => data);
    }

    /**
     * Отправить заявку на согласование
     */
    submitRequest(id) {
        return api.post(`/api/requests/items/${id}/submit`).then(({data}) => data);
    }

    /**
     * Вернуть заявку на доработку
     * @param {number} id - ID заявки
     * @param {string} comment - Причина возврата (обязательно)
     */
    returnRequest(id, comment) {
        return api.post(`/api/requests/items/${id}/return`, { comment }).then(({data}) => data);
    }

    /**
     * Завершить выполнение заявки (на приемку)
     */
    completeRequest(id) {
        return api.post(`/api/requests/items/${id}/complete`).then(({data}) => data);
    }

    /**
     * Принять выполненную заявку
     */
    acceptRequest(id) {
        return api.post(`/api/requests/items/${id}/accept`).then(({data}) => data);
    }

    /**
     * Частично выполнить заявку
     */
    partiallyCompleteRequest(id) {
        return api.post(`/api/requests/items/${id}/partially_complete`).then(({data}) => data);
    }

    /**
     * Принять частично выполненную заявку
     */
    acceptPartiallyRequest(id) {
        return api.post(`/api/requests/items/${id}/accept_partially`).then(({data}) => data);
    }

    /**
     * Отменить заявку
     */
    cancelRequest(id, comment) {
        return api.post(`/api/requests/items/${id}/cancel`, { comment }).then(({data}) => data);
    }

    /**
     * Изменить статус заявки напрямую (только для администратора)
     * @param {number} id - ID заявки
     * @param {string} targetStatus - Целевой статус
     * @param {string} [comment] - Комментарий к изменению
     * @returns {Promise<Object>} Результат изменения статуса
     */
    changeRequestStatus(id, targetStatus, comment = null) {
        const payload = { target_status: targetStatus };
        if (comment) {
            payload.comment = comment;
        }
        return api.post(`/api/requests/items/${id}/change-status`, payload).then(({data}) => data);
    }

    /**
     * Назначить исполнителя на заявку
     */
    assignRequest(id, executorId) {
        return api.post(`/api/requests/items/${id}/assign`, { executor_id: executorId }).then(({data}) => data);
    }

    /**
     * Получить список доступных исполнителей для ручного назначения
     * @param {number|string} requestId - ID заявки
     * @returns {Promise<{executors: Array<{user_id, first_name, last_name, priority}>}>}
     */
    getAvailableExecutors(requestId) {
        return api.get(`/api/requests/items/${requestId}/available-executors`).then(({data}) => data);
    }

    /**
     * Обновить отгруженное количество для материала (одиночное)
     */
    shipMaterialItem(requestId, itemId, shipped_quantity, shipping_comment) {
        const payload = { shipped_quantity };
        if (shipping_comment !== undefined) {
            payload.shipping_comment = shipping_comment;
        }
        return api.put(`/api/requests/items/${requestId}/items/${itemId}/ship`, payload).then(({data}) => data);
    }

    /**
     * Массовое обновление отгруженных количеств
     */
    bulkShipMaterialItems(requestId, items) {
        return api.put(`/api/requests/items/${requestId}/items/bulk-ship`, { items }).then(({data}) => data);
    }

    /**
     * Обновить принятое количество для материала (одиночное)
     */
    acceptMaterialItem(requestId, itemId, accepted_quantity, acceptance_comment) {
        const payload = { accepted_quantity };
        if (acceptance_comment !== undefined) {
            payload.acceptance_comment = acceptance_comment;
        }
        return api.put(`/api/requests/items/${requestId}/items/${itemId}/accept`, payload).then(({data}) => data);
    }

    /**
     * Массовое обновление принятых количеств
     */
    bulkAcceptMaterialItems(requestId, items) {
        return api.put(`/api/requests/items/${requestId}/items/bulk-accept`, { items }).then(({data}) => data);
    }

    /**
     * Массовое обновление материалов (добавление/обновление/удаление)
     */
    bulkUpdateMaterials(requestId, items) {
        return api.put(`/api/requests/items/${requestId}/items/bulk-update`, { items }).then(({data}) => data);
    }

    /**
     * Получить доступные строительные процессы с фильтрацией по ролям
     * @param {number} [requestTypeId] - ID типа заявки (опционально)
     */
    getAvailableBuildingProcesses(requestTypeId = null) {
        const params = {};
        if (requestTypeId) {
            params.request_type_id = requestTypeId;
        }
        return api.get(`/api/requests/building-processes`, { params }).then(({data}) => data);
    }

    /**
     * Получить справочник материалов без проверки прав доступа
     */
    getMaterials(requestTypeId = null) {
        const params = {};
        if (requestTypeId) {
            params.request_type_id = requestTypeId;
        }
        return api.get(`/api/requests/materials`, { params }).then(({data}) => data);
    }

    /**
     * Получить подключенный к типу заявки справочник позиций
     */
    getItemCatalog(requestTypeId, options = {}) {
        const params = { request_type_id: requestTypeId };
        if (options && options.context) {
            params.context = options.context;
        }
        if (options && options.requestId) {
            params.request_id = options.requestId;
        }
        if (options && (options.buildingProcessId || options.building_process_id)) {
            params.building_process_id = options.buildingProcessId || options.building_process_id;
        }
        if (options && Array.isArray(options.itemIds) && options.itemIds.length > 0) {
            params.item_ids = options.itemIds.join(',');
        }
        if (options && options.q) {
            params.q = options.q;
        }
        if (options && options.limit) {
            params.limit = options.limit;
        }
        if (options && (options.offset || options.offset === 0)) {
            params.offset = options.offset;
        }
        return api.get(`/api/requests/item-catalog`, {
            params
        }).then(({data}) => data);
    }

    /**
     * Получить все черновики текущего пользователя
     * @returns {Promise<Array>} Массив черновиков
     */
    getDrafts() {
        return api.get(`/api/requests/drafts`)
            .then(function(response) {
                return response.data && response.data.drafts ? response.data.drafts : [];
            });
    }

    /**
     * Получить черновик для конкретного типа заявки
     * @param {number} requestTypeId - ID типа заявки
     * @returns {Promise<Object|null>} Черновик или null если не найден
     */
    getDraft(requestTypeId) {
        return api.get(`/api/requests/drafts/${requestTypeId}`)
            .then(function(response) {
                // Сервер всегда возвращает 200, draft может быть null если не найден
                return response.data && response.data.draft ? response.data.draft : null;
            });
    }

    /**
     * Сохранить черновик
     * @param {number} requestTypeId - ID типа заявки
     * @param {Object} draftData - Данные черновика
     * @returns {Promise<Object>} Результат сохранения с полями draft и created
     */
    saveDraft(requestTypeId, draftData) {
        return api.post(`/api/requests/drafts`, {
            request_type_id: requestTypeId,
            draft_data: draftData
        }).then(function(response) {
            return response.data;
        });
    }

    /**
     * Обновить черновик
     * @param {number} draftId - ID черновика
     * @param {Object} draftData - Данные черновика
     * @returns {Promise<Object>} Обновленный черновик
     */
    updateDraft(draftId, draftData) {
        return api.put(`/api/requests/drafts/${draftId}`, {
            draft_data: draftData
        }).then(function(response) {
            return response.data;
        });
    }

    /**
     * Удалить черновик по ID
     * @param {number} draftId - ID черновика
     * @returns {Promise<Object>} Результат удаления
     */
    deleteDraft(draftId) {
        return api.delete(`/api/requests/drafts/${draftId}`)
            .then(function(response) {
                return response.data;
            });
    }

    /**
     * Удалить черновик по типу заявки
     * @param {number} requestTypeId - ID типа заявки
     * @returns {Promise<Object>} Результат удаления
     */
    deleteDraftByType(requestTypeId) {
        return api.delete(`/api/requests/drafts/type/${requestTypeId}`)
            .then(function(response) {
                // Сервер всегда возвращает 200 (idempotent операция)
                return response.data;
            });
    }

    /**
     * Копировать заявку
     * @param {number} id - ID заявки для копирования
     * @returns {Promise<Object>} Результат копирования с полями success, requestId, request
     */
    copyRequest(id) {
        return api.post(`/api/requests/items/${id}/copy`).then(({data}) => data);
    }
}

